import { FacilityDevicesByFloorsQueryResult } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

import { DeviceI, FloorI, TailgateConnectionI } from '../types/types'
import { DEFAULT_CAMERA_PROPERTIES } from './defaultCameraProperties'

type DeviceNodeType =
  | FacilityDevicesByFloorsQueryResult['data']['facility']['devices']['edges'][0]['node']

type FacilityType = Pick<
  FacilityDevicesByFloorsQueryResult['data']['facility'],
  'id' | 'name'
>

/**
 * Association maps for device relationships.
 */
interface DeviceAssociationMapsI {
  /** Map from door ID to set of camera IDs connected to that door */
  doorToCameras: Map<string, Set<string>>
  /** Map from camera ID to set of door IDs connected to that camera */
  cameraToDoors: Map<string, Set<string>>
}

export const getDeviceFloorIds = (
  selectedFloorId: string,
  floors: FloorI[],
  types: DeviceType[]
): string[] => {
  if (types.includes(DeviceType.Checkpoint)) {
    return floors?.map((f) => f.id)
  }
  return [selectedFloorId]
}

export const mapDevices = (
  data: FacilityDevicesByFloorsQueryResult['data']
): DeviceI[] =>
  data?.facility?.devices?.edges?.map((e) => mapDevice(e?.node, data?.facility))

export const mapDevice = (
  deviceNode: DeviceNodeType,
  facility: FacilityType
): DeviceI => ({
  x: deviceNode.x || DEFAULT_CAMERA_PROPERTIES.x,
  y: deviceNode.y || DEFAULT_CAMERA_PROPERTIES.y,
  rotation: deviceNode.bearing || DEFAULT_CAMERA_PROPERTIES.rotation,
  angle: deviceNode.fieldOfView || DEFAULT_CAMERA_PROPERTIES.angle,
  distance: deviceNode.visibilityRange || DEFAULT_CAMERA_PROPERTIES.distance,
  id: deviceNode.id,
  deviceName: deviceNode.name,
  isEditMode: false,
  editCheckpointPos: false,
  deviceHealth: deviceNode.status,
  placed: deviceNode.x != null && deviceNode.y != null,
  deviceType: deviceNode.type,
  tailgateConnections: deviceNode?.tailgateConns?.edges?.map((edge) => ({
    id: edge.node.id,
    door: edge.node.door.id,
  })) as TailgateConnectionI[],
  createdAt: deviceNode.createdAt,
  camera: deviceNode.camera,
  checkpoint: {
    id: deviceNode?.checkpoint?.id,
    displayId: deviceNode?.checkpoint?.displayId,
    isPhotoRequired: deviceNode?.checkpoint?.isPhotoRequired,
    facility: {
      id: facility?.id,
      name: facility?.name,
    },
    floor: {
      id: deviceNode?.floor?.id,
      name: deviceNode?.floor?.name,
    },
  },
})

/**
 * Builds bidirectional index maps for device associations via tailgate connections.
 * This enables O(1) lookups for finding associated devices instead of O(n) searches.
 *
 * @param devices - Array of all devices
 * @returns Object containing doorToCameras and cameraToDoors maps
 */
export const buildDeviceAssociationMaps = (
  devices: DeviceI[]
): DeviceAssociationMapsI => {
  const doorToCameras = new Map<string, Set<string>>()
  const cameraToDoors = new Map<string, Set<string>>()

  devices.forEach((device) => {
    if (device.tailgateConnections) {
      device.tailgateConnections.forEach((conn) => {
        if (conn.door) {
          // This device (camera) connects to a door
          if (!cameraToDoors.has(device.id)) {
            cameraToDoors.set(device.id, new Set())
          }
          cameraToDoors.get(device.id)!.add(conn.door)

          // The door connects to this device (camera)
          if (!doorToCameras.has(conn.door)) {
            doorToCameras.set(conn.door, new Set())
          }
          doorToCameras.get(conn.door)!.add(device.id)
        }
      })
    }
  })

  return { doorToCameras, cameraToDoors }
}

/**
 * Filters devices to include those matching the name filter and their associated devices.
 * Associated devices are determined through tailgate connections (cameras ↔ doors).
 * Uses index maps for O(n) performance instead of O(n²).
 *
 * @param devices - Array of all devices
 * @param nameLikeFilter - Name filter string
 * @returns Filtered array of devices including matches and associated devices
 */
export const filterDevicesWithAssociations = (
  devices: DeviceI[],
  nameLikeFilter: string
): DeviceI[] => {
  if (!nameLikeFilter) {
    return devices
  }

  // Build index maps for O(1) lookups - O(n) time
  const { doorToCameras, cameraToDoors } = buildDeviceAssociationMaps(devices)

  // Find devices matching the name filter - O(n) time
  const matchingDevices = devices.filter((device) =>
    device.deviceName?.toLowerCase().includes(nameLikeFilter.toLowerCase())
  )

  const associatedDeviceIds = new Set<string>()

  // For each matching device, add it and its associations - O(m * k) time
  // where m = number of matching devices, k = average associations per device
  matchingDevices.forEach((device) => {
    // Add the matching device itself
    associatedDeviceIds.add(device.id)

    // Add associated cameras if this is a door - O(k) time via map lookup
    const connectedCameras = doorToCameras.get(device.id)
    if (connectedCameras) {
      connectedCameras.forEach((cameraId) => {
        associatedDeviceIds.add(cameraId)
      })
    }

    // Add associated doors if this is a camera - O(k) time via map lookup
    const connectedDoors = cameraToDoors.get(device.id)
    if (connectedDoors) {
      connectedDoors.forEach((doorId) => {
        associatedDeviceIds.add(doorId)
      })
    }
  })

  // Filter to only include devices that match or are associated - O(n) time
  return devices.filter((device) => associatedDeviceIds.has(device.id))
}
